import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import RateShopperModeToggle from './RateShopperModeToggle';
import { WEEKDAY_ABBR, buildMonthGrids } from '../utils/calendarGrid';

// Rate Shopper — the shopping product surface (M3). Reads the M2 mapping-aware
// snapshots. Tabs: RS-01 Overview, RS-02 Rate Calendar, Price Trend (line chart
// over the same calendar data), RS-03 Competitor Matrix, Competitor OTA Matrix
// (per-channel rows), RS-05 Live Shop; RS-04 Date Detail opens as a drawer from
// calendar/matrix cells.
// Spec: docs/SRS_PRICING_INTELLIGENCE_SUITE.md §3/§9, docs/DEV_RATESHOPPER_PHASE1.md M3.

const inr = (v) => v == null ? '—' : `₹${Number(v).toLocaleString('en-IN')}`;
const signed = (v) => v == null ? '—' : `${v > 0 ? '+' : ''}₹${Number(v).toLocaleString('en-IN')}`;
const shortDate = (iso) => { if (!iso) return ''; const d = new Date(iso + 'T00:00:00'); return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); };
const dow = (iso) => { if (!iso) return ''; return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short' }); };
const isWeekend = (iso) => { const d = new Date(iso + 'T00:00:00').getDay(); return d === 0 || d === 6; };
const ago = (ts) => {
  if (!ts) return 'never';
  const mins = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
};
const chip = (bg, fg) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: bg, color: fg, whiteSpace: 'nowrap' });
const rankChip = (rank, total) => {
  if (rank == null) return chip('rgba(148,163,184,0.18)', 'var(--theme-text-muted)');
  if (rank === 1) return chip('rgba(22,163,74,0.16)', 'var(--theme-success)');
  if (total && rank >= total) return chip('rgba(239,68,68,0.14)', 'var(--theme-danger)');
  return chip('rgba(37,99,235,0.14)', '#2563eb');
};
const deltaColor = (d) => d == null ? 'var(--theme-text-muted)' : d > 0 ? 'var(--theme-danger)' : d < 0 ? 'var(--theme-success)' : 'var(--theme-text)';
// Same channel→color mapping as the Comp Set rate grid (PmsRevenueManager.js), kept in sync so an OTA reads the same color everywhere.
const chanColor = (ch) => {
  const c = String(ch || '').toLowerCase();
  if (c.includes('booking')) return '#1e40af';
  if (c.includes('agoda')) return '#be185d';
  if (c.includes('expedia')) return '#b45309';
  if (c.includes('mmt') || c.includes('makemytrip') || c.includes('goibibo')) return '#dc2626';
  return '#64748b';
};
const sevChip = (sev) => ({ HIGH: chip('rgba(239,68,68,0.14)', 'var(--theme-danger)'), MEDIUM: chip('rgba(202,138,4,0.14)', '#ca8a04'), LOW: chip('rgba(37,99,235,0.12)', '#2563eb') }[sev] || chip('rgba(148,163,184,0.18)', 'var(--theme-text-muted)'));

// Keyboard + screen-reader accessible activation for non-button clickables.
const clickable = (onActivate, label) => ({
  role: 'button', tabIndex: 0, 'aria-label': label,
  onClick: onActivate,
  onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate(); } },
});

// CSV export (non-financial tabular data → client-side Blob download).
function toCsv(rows, columns) {
  const esc = (v) => { const s = v == null ? '' : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const head = columns.map((c) => esc(c.label)).join(',');
  const body = (rows || []).map((r) => columns.map((c) => esc(typeof c.get === 'function' ? c.get(r) : r[c.key])).join(',')).join('\n');
  return `${head}\n${body}`;
}
function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function RateShopper({ token, selectedProperty, onSwitchScreen, onOpenInRms, focus, onClearFocus }) {
  const [tab, setTab] = useState('overview');
  const [meal, setMeal] = useState('EP');
  const [mealPlans, setMealPlans] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState('');
  const [days, setDays] = useState(30);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [overview, setOverview] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [matrix, setMatrix] = useState({ dates: [], own_by_date: {}, competitors: [] });
  const [shopLog, setShopLog] = useState([]);
  const [shopMeta, setShopMeta] = useState({ last_synced_at: null, syncs_used_today: 0, max_syncs_per_day: 5, syncs_remaining_today: 5 });
  const [shopping, setShopping] = useState(false);
  const [alerts, setAlerts] = useState(null);
  const [parity, setParity] = useState(null);
  const [sources, setSources] = useState(null);
  const [drawer, setDrawer] = useState(null); // { date }
  const [detail, setDetail] = useState(null);
  const [detailTab, setDetailTab] = useState('market');
  const [otaMatrix, setOtaMatrix] = useState({ dates: [], competitors: [], own: [] });
  const [otaSort, setOtaSort] = useState({ key: 'hotel', dir: 'asc' });
  const [otaFilterChannel, setOtaFilterChannel] = useState('');
  const [refDate, setRefDate] = useState('');
  const [showGap, setShowGap] = useState(true);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const api = useCallback(async (path, opts = {}) => {
    const res = await fetch(`/api/v1/rate-shopper${path}`, { ...opts, headers: { ...headers(), ...(opts.headers || {}) } });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  }, [headers]);

  // Bootstrap: rooms + meal plans.
  useEffect(() => {
    if (!selectedProperty?.id) return;
    (async () => {
      try {
        const [r, mp] = await Promise.all([api('/own-rooms'), api('/meal-plans')]);
        setRooms(r.rooms || []);
        setMealPlans(mp.meal_plans || []);
        if (!room && (r.rooms || []).length) setRoom(String(r.rooms[0].own_room_id));
      } catch (e) { setError(e.message); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty, api]);

  const q = useMemo(() => {
    const p = new URLSearchParams({ meal, days: String(days) });
    if (room) p.set('room', room);
    return p.toString();
  }, [meal, days, room]);

  const loadTab = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true); setError('');
    try {
      if (tab === 'overview') {
        const [ov, par] = await Promise.all([api(`/overview?${q}`), api(`/parity?${q}`).catch(() => null)]);
        setOverview(ov);
        if (par) setParity(par);
      }
      else if (tab === 'calendar' || tab === 'trend') setCalendar((await api(`/rates/calendar?${q}`)).days || []);
      else if (tab === 'matrix') {
        const p = new URLSearchParams(q);
        if (refDate) p.set('ref_date', refDate);
        const m = await api(`/rates/matrix?${p.toString()}`);
        setMatrix(m);
        if (m.ref_date && m.ref_date !== refDate) setRefDate(m.ref_date);
      }
      else if (tab === 'shop') {
        const s = await api('/shop/live/status');
        setShopLog(s.log || []);
        setShopMeta({ last_synced_at: s.last_synced_at, syncs_used_today: s.syncs_used_today, max_syncs_per_day: s.max_syncs_per_day, syncs_remaining_today: s.syncs_remaining_today });
      }
      else if (tab === 'alerts') setAlerts(await api(`/alerts?${q}`));
      else if (tab === 'parity') setParity(await api(`/parity?${q}`));
      else if (tab === 'sources') setSources(await api('/source-health'));
      else if (tab === 'ota') setOtaMatrix(await api(`/rates/ota-matrix?${q}`));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [tab, q, api, selectedProperty, refDate]);

  useEffect(() => { loadTab(); }, [loadTab]);

  const openDate = async (date, overrides = {}) => {
    const effMeal = overrides.meal || meal;
    const effRoom = overrides.room || room;
    setDrawer({ date }); setDetail(null); setDetailTab('market');
    try {
      const p = new URLSearchParams({ date, meal: effMeal });
      if (effRoom) p.set('room', effRoom);
      setDetail(await api(`/rates/date-detail?${p.toString()}`));
    } catch (e) { setError(e.message); }
  };

  // Consume a deep link from Price Intelligence's recommendation drawer:
  // land on Overview and open the RS-04 date-detail drawer (the competitor
  // evidence behind that recommendation) for the focused date/room/meal.
  // openDate takes explicit overrides rather than waiting on room/meal state
  // to update first, since setRoom/setMeal below wouldn't be visible yet
  // inside this same synchronous effect run.
  useEffect(() => {
    if (!focus?.date) return;
    setTab('overview');
    if (focus.room != null) setRoom(String(focus.room));
    if (focus.meal) setMeal(focus.meal);
    openDate(focus.date, { room: focus.room != null ? String(focus.room) : undefined, meal: focus.meal });
    if (typeof onClearFocus === 'function') onClearFocus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]);

  const runShop = async () => {
    setShopping(true); setError('');
    try {
      const r = await api('/shop/live', { method: 'POST', body: '{}' });
      if (!r.ok) setError('Shop request was not accepted.');
    } catch (e) { setError(e.message); }
    finally {
      setShopping(false);
      // Always refresh status after an attempt (even a failed/limited one) so
      // the used/remaining count and log reflect what actually happened.
      try {
        const s = await api('/shop/live/status');
        setShopLog(s.log || []);
        setShopMeta({ last_synced_at: s.last_synced_at, syncs_used_today: s.syncs_used_today, max_syncs_per_day: s.max_syncs_per_day, syncs_remaining_today: s.syncs_remaining_today });
      } catch { /* best-effort refresh */ }
    }
  };

  const otaChannelOptions = useMemo(
    () => [...new Set([...otaMatrix.competitors, ...otaMatrix.own].map((c) => c.channel))].sort((a, b) => a.localeCompare(b)),
    [otaMatrix.competitors, otaMatrix.own]
  );
  // Your Hotel rows — always pinned above competitors regardless of Hotel/OTA
  // Wise sort (it's one fixed entity, not part of the sortable competitor set),
  // but still respects the OTA channel filter like everything else in this grid.
  const ownOtaRows = useMemo(
    () => (otaFilterChannel ? otaMatrix.own.filter((o) => o.channel === otaFilterChannel) : otaMatrix.own)
      .slice().sort((a, b) => a.channel.localeCompare(b.channel)),
    [otaMatrix.own, otaFilterChannel]
  );
  const otaRows = useMemo(() => {
    const filtered = otaFilterChannel ? otaMatrix.competitors.filter((c) => c.channel === otaFilterChannel) : otaMatrix.competitors;
    const dir = otaSort.dir === 'desc' ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const av = otaSort.key === 'ota' ? a.channel : a.hotel_name;
      const bv = otaSort.key === 'ota' ? b.channel : b.hotel_name;
      return dir * av.localeCompare(bv, undefined, { sensitivity: 'base' });
    });
  }, [otaMatrix.competitors, otaFilterChannel, otaSort]);
  const toggleOtaSort = (key) => setOtaSort((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  const otaSortArrow = (key) => (otaSort.key === key ? (otaSort.dir === 'asc' ? ' ▲' : ' ▼') : '');

  if (!selectedProperty?.id) {
    return <div style={{ padding: 24, color: 'var(--theme-text-muted)' }}>Select a property to shop competitor rates.</div>;
  }

  const roomName = rooms.find((x) => String(x.own_room_id) === String(room))?.name || 'room';

  // "Bulk Update"/"Smart Pricing" never write a rate from here — per product
  // decision, they hand off to RMS's own guardrailed apply pipeline, pre-scoped
  // to whatever date is currently in view (the open drawer, else the first date
  // on screen, else today).
  const todayLocal = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
  const openInRms = () => {
    if (typeof onOpenInRms !== 'function') return;
    const stayDate = drawer?.date || calendar[0]?.stay_date || matrix.dates?.[0] || todayLocal();
    onOpenInRms({ stay_date: stayDate, room: room ? Number(room) : undefined, room_name: roomName, meal });
  };

  return (
    <div style={{ padding: 20 }}>
      <RateShopperModeToggle mode="shopper" onSwitch={onSwitchScreen} />
      {/* Header + controls */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Rate Shopper</h2>
          <div style={{ color: 'var(--theme-text-muted)', fontSize: 13 }}>Competitor price intelligence on your mapped products.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={room} onChange={(e) => setRoom(e.target.value)} style={sel} title="Own room type">
            {rooms.map((r) => <option key={r.own_room_id} value={r.own_room_id}>{r.name}</option>)}
          </select>
          <select value={meal} onChange={(e) => setMeal(e.target.value)} style={sel} title="Meal plan">
            {(mealPlans.length ? mealPlans : [{ code: 'EP', label: 'European Plan' }]).map((mp) => <option key={mp.code} value={mp.code}>{mp.code}</option>)}
          </select>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={sel} title="Horizon">
            {[30, 60, 90].map((d) => <option key={d} value={d}>{d} days</option>)}
          </select>
          <button onClick={loadTab} style={btn('ghost')}>↻</button>
          {typeof onOpenInRms === 'function' && (
            <>
              <button onClick={openInRms} style={btn('ghost')} title="Review and apply this date's rate in Revenue Manager (guardrailed)">Bulk Update</button>
              <button onClick={openInRms} style={btn('primary')} title="Review and apply this date's AI recommendation in Revenue Manager (guardrailed)">✨ Smart Pricing</button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, margin: '12px 0', borderBottom: '1px solid var(--theme-border)', paddingBottom: 8, flexWrap: 'wrap' }}>
        {[['overview', 'Overview'], ['calendar', 'Rate Calendar'], ['trend', 'Price Trend'], ['matrix', 'Competitor Matrix'], ['ota', 'Competitor OTA Matrix'], ['alerts', 'Alerts'], ['parity', 'Parity'], ['sources', 'Sources'], ['shop', 'Live Shop']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={btn(tab === k ? 'primary' : 'ghost')} aria-pressed={tab === k}>{l}</button>
        ))}
      </div>

      {error && <div style={banner('danger')}>{error} <span onClick={() => setError('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}
      {loading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--theme-text-muted)' }}>Loading…</div>}

      {/* RS-01 Overview */}
      {!loading && tab === 'overview' && overview && (
        <div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
            {[
              ['Best Market Rate', inr(overview.kpis.best_market_rate), `${roomName} · Today`],
              ['Your Rate', inr(overview.kpis.your_rate_today), overview.kpis.your_rank_today != null ? `Rank #${overview.kpis.your_rank_today}/${overview.kpis.your_rank_of_today}` : 'No rank data'],
              ['Price Gap', signed(overview.kpis.price_gap_today), 'vs. Market Median'],
              ['Comp Set', overview.kpis.competitors, 'Active Competitors'],
            ].map(([label, value, sub]) => (
              <div key={label} style={kpiCard}>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
                <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>{label}</div>
                {sub && <div style={{ fontSize: 11, color: 'var(--theme-text-muted)', marginTop: 2 }}>{sub}</div>}
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--theme-text-muted)', marginBottom: 14 }}>
            {days}-day window avg: own {inr(overview.kpis.own_avg_rate)} · market {inr(overview.kpis.market_median_avg)} · cheapest {overview.kpis.days_cheapest}/{overview.kpis.days_scored} days · {overview.kpis.comparable_observations} comparable obs.
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, fontSize: 12, color: 'var(--theme-text-muted)' }}>
            <span style={chip(overview.source_health.recent_batches ? 'rgba(22,163,74,0.14)' : 'rgba(202,138,4,0.14)', overview.source_health.recent_batches ? 'var(--theme-success)' : '#ca8a04')}>
              {overview.source_health.recent_batches ? 'Feed healthy' : 'Feed stale'}
            </span>
            Last competitor batch: {ago(overview.source_health.last_batch)} · {overview.meal} · next {days} days
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '14px 0' }}>
            {[
              ['Below Market', overview.kpis.below_market_nights, 'nights · Adjust recommended'],
              ['Above Market', overview.kpis.above_market_nights, 'nights'],
              ['Parity Issues', parity?.summary?.violations ?? (parity?.has_cm_data === false ? '—' : 0), parity?.has_cm_data === false ? 'No CM data yet' : 'OTA rate mismatch'],
              ['Opportunity', signed(overview.kpis.opportunity_revpar_estimate), 'Potential RevPAR lift'],
            ].map(([label, value, sub]) => (
              <div key={label} style={kpiCard}>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{value}</div>
                <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>{label}</div>
                <div style={{ fontSize: 10, color: 'var(--theme-text-muted)', fontStyle: label === 'Opportunity' ? 'italic' : 'normal' }}>{sub}{label === 'Opportunity' ? ' — estimate, not a guarantee' : ''}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '14px 0 6px' }}>
            <h3 style={{ fontSize: 14, margin: 0 }}>
              Action Required — {overview.action_queue_total ?? overview.action_queue.length} day(s) need attention
              {overview.action_queue_total > overview.action_queue.length && <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--theme-text-muted)' }}> (showing top {overview.action_queue.length} by gap size)</span>}
            </h3>
            {overview.action_queue.length > 0 && (
              <button style={btn('ghost')} onClick={() => downloadCsv('rate-shopper-actions.csv', toCsv(overview.action_queue,
                [{ label: 'Date', key: 'stay_date' }, { label: 'Room', key: 'own_room_name' }, { label: 'Signal', key: 'type' }, { label: 'Own', key: 'own_rate' }, { label: 'Market median', key: 'market_median' }, { label: 'Gap', key: 'delta' }]))}>⭳ CSV</button>
            )}
          </div>
          {overview.action_queue.length === 0 ? (
            <div style={emptyBox}>No significant pricing gaps. Own rates are close to the market median.</div>
          ) : (
            <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border)', borderRadius: 8 }}>
              <table style={table}>
                <thead><tr>{['Date', 'Room', 'Signal', 'Own', 'Market median', 'Gap'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {overview.action_queue.map((a, i) => (
                    <tr key={i} style={{ cursor: 'pointer' }} {...clickable(() => openDate(a.stay_date), `Open ${a.own_room_name} ${a.stay_date}`)}>
                      <td style={td}>{shortDate(a.stay_date)} <span style={{ color: 'var(--theme-text-muted)', fontSize: 11 }}>{dow(a.stay_date)}</span></td>
                      <td style={td}>{a.own_room_name}</td>
                      <td style={td}>{a.type === 'ABOVE_MARKET'
                        ? <span style={chip('rgba(239,68,68,0.14)', 'var(--theme-danger)')}>Above market</span>
                        : <span style={chip('rgba(22,163,74,0.14)', 'var(--theme-success)')}>Below market</span>}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{inr(a.own_rate)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{inr(a.market_median)}</td>
                      <td style={{ ...td, textAlign: 'right', color: deltaColor(a.delta) }}>{signed(a.delta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* RS-02 Rate Calendar */}
      {!loading && tab === 'calendar' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8 }}>
            <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>
              {roomName} · {meal} — each cell: own rate vs market median, price rank. Click a day for detail.
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={showGap} onChange={(e) => setShowGap(e.target.checked)} /> Show Gap
              </label>
              {calendar.length > 0 && (
                <button style={btn('ghost')} onClick={() => downloadCsv('rate-shopper-calendar.csv', toCsv(calendar,
                  [{ label: 'Date', key: 'stay_date' }, { label: 'Own rate', key: 'own_rate' }, { label: 'Market median', key: 'market_median' }, { label: 'Rank', key: 'own_rank' }, { label: 'Comparable', key: 'comparable_count' }, { label: 'Cheapest comp', key: 'cheapest_competitor' }, { label: 'Delta', key: 'delta' }]))}>⭳ CSV</button>
              )}
            </div>
          </div>
          {calendar.length === 0 ? (
            <div style={emptyBox}>
              No snapshot data yet. Create mappings and run a rate shop, then rebuild snapshots.
              {typeof onSwitchScreen === 'function' && (
                <div style={{ marginTop: 10 }}>
                  <button type="button" style={btn('primary')} onClick={() => onSwitchScreen('rate-shopper-mapping')}>Set up mapping →</button>
                </div>
              )}
            </div>
          ) : (
            buildMonthGrids(calendar).map((mg) => (
              <div key={mg.key} style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: 'var(--theme-text)' }}>{mg.label}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
                  {WEEKDAY_ABBR.map((w) => (
                    <div key={w} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--theme-text-muted)', padding: '2px 0 6px' }}>{w}</div>
                  ))}
                  {mg.cells.map((d, i) => {
                    if (!d) return <div key={i} />;
                    if (d._outsideHorizon) {
                      const outDayNum = Number(d.stay_date.slice(8, 10));
                      return (
                        <div key={i} style={{ minHeight: 90, borderRadius: 8, border: '1px solid var(--theme-border, #e2e8f0)', opacity: 0.4, padding: 10 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--theme-text-muted)' }}>{outDayNum}</div>
                        </div>
                      );
                    }
                    const dayNum = Number(d.stay_date.slice(8, 10));
                    return (
                      <div key={i} {...clickable(() => openDate(d.stay_date), `${roomName} ${d.stay_date}: own ${d.own_rate}, market ${d.market_median}`)} style={{
                        border: '1px solid var(--theme-border)', borderRadius: 8, padding: 10, cursor: 'pointer', minHeight: 90,
                        background: isWeekend(d.stay_date) ? 'var(--theme-surface-2, #f8fafc)' : 'var(--theme-surface)',
                      }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{dayNum}</div>
                        <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>{inr(d.own_rate)}</div>
                        <div style={{ fontSize: 11, color: 'var(--theme-text-muted)' }}>mkt {inr(d.market_median)}</div>
                        {showGap && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                            <span style={rankChip(d.own_rank, d.comparable_count + 1)}>
                              {d.own_rank != null ? `#${d.own_rank}/${d.comparable_count + 1}` : 'n/a'}
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 600, color: deltaColor(d.delta) }}>{signed(d.delta)}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
          {calendar.length > 0 && (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 4, fontSize: 12, color: 'var(--theme-text-muted)' }}>
              <span><span style={{ color: 'var(--theme-danger)', fontWeight: 700 }}>●</span> Above Market — your rate is over the median</span>
              <span><span style={{ color: 'var(--theme-text)', fontWeight: 700 }}>●</span> Parity — close to the median</span>
              <span><span style={{ color: 'var(--theme-success)', fontWeight: 700 }}>●</span> Below Market — your rate is under the median</span>
              <span><span style={{ color: 'var(--theme-text-muted)', fontWeight: 700 }}>●</span> No Data — no comparable competitor rates</span>
            </div>
          )}
        </div>
      )}

      {/* Price Trend — line chart over the same /rates/calendar data as Rate
          Calendar, for at-a-glance trend reading instead of scanning day cards. */}
      {!loading && tab === 'trend' && (
        <div>
          <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', marginBottom: 8 }}>
            {roomName} · {meal} — own rate vs market median and the cheapest tracked competitor, over the next {days} days.
          </div>
          {calendar.length === 0 ? (
            <div style={emptyBox}>
              No snapshot data yet. Create mappings and run a rate shop, then rebuild snapshots.
              {typeof onSwitchScreen === 'function' && (
                <div style={{ marginTop: 10 }}>
                  <button type="button" style={btn('primary')} onClick={() => onSwitchScreen('rate-shopper-mapping')}>Set up mapping →</button>
                </div>
              )}
            </div>
          ) : (
            <div style={{ width: '100%', height: 340, border: '1px solid var(--theme-border)', borderRadius: 8, padding: '12px 8px 4px' }}>
              <ResponsiveContainer>
                <LineChart data={calendar} margin={{ top: 5, right: 20, bottom: 5, left: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--theme-border, #e2e8f0)" />
                  <XAxis dataKey="stay_date" tickFormatter={shortDate} tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${Math.round(v / 100) / 10}k`} width={48} />
                  <Tooltip labelFormatter={shortDate} formatter={(v, name) => [inr(v), name]} />
                  <Legend />
                  <Line type="monotone" dataKey="own_rate" name="Your rate" stroke="#2563eb" strokeWidth={2} dot={false} connectNulls />
                  <Line type="monotone" dataKey="market_median" name="Market median" stroke="#94a3b8" strokeDasharray="4 4" strokeWidth={2} dot={false} connectNulls />
                  <Line type="monotone" dataKey="cheapest_competitor" name="Cheapest competitor" stroke="#dc2626" strokeDasharray="2 2" strokeWidth={1.5} dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* RS-03 Competitor Matrix */}
      {!loading && tab === 'matrix' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>
              {roomName} · {meal} — best comparable competitor rate per day; number below is gap vs your rate. Click a date header for detail.
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {matrix.dates.length > 0 && (
                <select value={refDate} onChange={(e) => setRefDate(e.target.value)} style={sel} title="Rank/Gap/Trend reference date">
                  {matrix.dates.map((dt) => <option key={dt} value={dt}>{shortDate(dt)} rank</option>)}
                </select>
              )}
            {matrix.competitors.length > 0 && (
              <button style={btn('ghost')} onClick={() => downloadCsv('rate-shopper-matrix.csv', toCsv(
                matrix.competitors.flatMap((c) => matrix.dates.map((dt) => ({ hotel: c.hotel_name, date: dt, rate: c.cells[dt]?.rate ?? '', comparable: c.cells[dt]?.is_comparable ?? '', nc_reason: c.cells[dt]?.nc_reason ?? '', channel: c.cells[dt]?.channel ?? '', gap_vs_you: c.cells[dt]?.delta ?? '', rank_at_ref: c.rank_at_ref ?? '', gap_pct_at_ref: c.gap_pct_at_ref ?? '' }))),
                [{ label: 'Hotel', key: 'hotel' }, { label: 'Date', key: 'date' }, { label: 'Rate', key: 'rate' }, { label: 'Comparable', key: 'comparable' }, { label: 'N/C reason', key: 'nc_reason' }, { label: 'Channel', key: 'channel' }, { label: 'Gap vs you', key: 'gap_vs_you' }, { label: `Rank (${matrix.ref_date || ''})`, key: 'rank_at_ref' }, { label: 'Gap % (ref date)', key: 'gap_pct_at_ref' }]))}>⭳ CSV</button>
            )}
            </div>
          </div>
          {matrix.competitors.length === 0 || matrix.dates.length === 0 ? (
            <div style={emptyBox}>No competitor snapshots for this room/meal window.</div>
          ) : (
            <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border)', borderRadius: 8 }}>
              <table style={{ ...table, minWidth: 760 }}>
                <thead>
                  <tr>
                    <th style={{ ...th, position: 'sticky', left: 0, zIndex: 2, background: 'var(--theme-surface-2, #f1f5f9)', minWidth: 150 }}>Hotel</th>
                    <th style={{ ...th, textAlign: 'center', minWidth: 56 }}>Rank</th>
                    <th style={{ ...th, textAlign: 'center', minWidth: 64 }}>Gap %</th>
                    <th style={{ ...th, textAlign: 'center', minWidth: 80 }}>Trend (7D)</th>
                    {matrix.dates.map((dt) => (
                      <th key={dt} style={{ ...th, textAlign: 'center', cursor: 'pointer' }} {...clickable(() => openDate(dt), `Detail for ${dt}`)}>
                        {shortDate(dt)}<div style={{ fontSize: 10, fontWeight: 400, color: 'var(--theme-text-muted)' }}>{dow(dt)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ ...td, position: 'sticky', left: 0, background: 'var(--theme-surface)', fontWeight: 700 }}>You ({roomName})</td>
                    <td style={{ ...td, textAlign: 'center' }}><span style={rankChip(matrix.own_rank_at_ref, matrix.competitors.length + 1)}>{matrix.own_rank_at_ref != null ? `#${matrix.own_rank_at_ref}` : 'n/a'}</span></td>
                    <td style={{ ...td, textAlign: 'center', color: deltaColor(matrix.own_gap_pct_at_ref), fontWeight: 600 }}>{matrix.own_gap_pct_at_ref != null ? `${matrix.own_gap_pct_at_ref > 0 ? '+' : ''}${matrix.own_gap_pct_at_ref}%` : '—'}</td>
                    <td style={td}>—</td>
                    {matrix.dates.map((dt) => <td key={dt} style={{ ...td, textAlign: 'center', fontWeight: 700 }}>{inr(matrix.own_by_date[dt])}</td>)}
                  </tr>
                  {matrix.competitors.map((c) => (
                    <tr key={c.competitor_id}>
                      <td style={{ ...td, position: 'sticky', left: 0, background: 'var(--theme-surface)', fontWeight: 600 }}>{c.hotel_name}</td>
                      <td style={{ ...td, textAlign: 'center' }}><span style={rankChip(c.rank_at_ref, matrix.competitors.length + 1)}>{c.rank_at_ref != null ? `#${c.rank_at_ref}` : 'n/a'}</span></td>
                      <td style={{ ...td, textAlign: 'center', color: deltaColor(c.gap_pct_at_ref), fontWeight: 600 }}>{c.gap_pct_at_ref != null ? `${c.gap_pct_at_ref > 0 ? '+' : ''}${c.gap_pct_at_ref}%` : '—'}</td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        {c.trend_7d && c.trend_7d.length > 1 ? (
                          <div style={{ width: 72, height: 26, display: 'inline-block' }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={c.trend_7d}>
                                <Line type="monotone" dataKey="rate" stroke="#64748b" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        ) : <span style={{ fontSize: 10, color: 'var(--theme-text-muted)' }}>—</span>}
                      </td>
                      {matrix.dates.map((dt) => {
                        const cell = c.cells[dt];
                        if (!cell || !cell.is_comparable || cell.rate == null) {
                          return <td key={dt} style={{ ...td, textAlign: 'center', color: 'var(--theme-text-muted)', fontSize: 11 }}>{cell?.nc_reason ? cell.nc_reason.replace(/_/g, ' ').toLowerCase() : '—'}</td>;
                        }
                        return (
                          <td key={dt} style={{ ...td, textAlign: 'center' }}>
                            <div style={{ fontWeight: 600 }}>{inr(cell.rate)}</div>
                            {cell.channel && <div style={{ fontSize: 10, fontWeight: 600, color: chanColor(cell.channel) }}>{cell.channel}</div>}
                            <div style={{ fontSize: 11, color: deltaColor(cell.delta) }}>{signed(cell.delta)}</div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {matrix.competitors.length > 0 && matrix.dates.length > 0 && (() => {
            const ratesAtRef = matrix.competitors
              .map((c) => ({ hotel: c.hotel_name, rate: c.cells[refDate]?.rate }))
              .filter((c) => c.rate != null);
            const lowest = ratesAtRef.length ? ratesAtRef.reduce((a, b) => (b.rate < a.rate ? b : a)) : null;
            const highest = ratesAtRef.length ? ratesAtRef.reduce((a, b) => (b.rate > a.rate ? b : a)) : null;
            const sorted = [...ratesAtRef.map((c) => c.rate)].sort((a, b) => a - b);
            const marketMedian = sorted.length ? (sorted.length % 2 ? sorted[(sorted.length - 1) / 2] : (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2) : null;
            return (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 14 }}>
                <h3 style={{ fontSize: 14, margin: '0 0 -4px', width: '100%' }}>Competitor Insights — {shortDate(refDate)}</h3>
                <div style={kpiCard}><div style={{ fontSize: 16, fontWeight: 700 }}>{lowest ? inr(lowest.rate) : '—'}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Lowest Rate</div><div style={{ fontSize: 11, color: 'var(--theme-text-muted)' }}>{lowest?.hotel || '—'}</div></div>
                <div style={kpiCard}><div style={{ fontSize: 16, fontWeight: 700 }}>{highest ? inr(highest.rate) : '—'}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Highest Rate</div><div style={{ fontSize: 11, color: 'var(--theme-text-muted)' }}>{highest?.hotel || '—'}</div></div>
                <div style={kpiCard}><div style={{ fontSize: 16, fontWeight: 700 }}>{marketMedian != null ? inr(marketMedian) : '—'}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Market Median</div></div>
                <div style={kpiCard}><div style={{ fontSize: 16, fontWeight: 700 }}>{matrix.own_rank_at_ref != null ? `#${matrix.own_rank_at_ref} of ${matrix.competitors.length + 1}` : 'n/a'}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Your Position</div></div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Competitor OTA Matrix — same date-columns layout as Competitor Matrix,
          but one row per (competitor, OTA channel) instead of one collapsed
          row per competitor, with an OTA column identifying the channel. */}
      {!loading && tab === 'ota' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>
              Raw competitor rates per OTA channel, across all mapped room categories — not filtered by own room. ★ Your Hotel rows (pinned to the top) show your own live rate per channel. Click Hotel/OTA to sort.
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ display: 'flex', border: '1px solid var(--theme-border)', borderRadius: 8, overflow: 'hidden' }}>
                <button onClick={() => setOtaSort({ key: 'hotel', dir: 'asc' })} style={{ ...btn(otaSort.key === 'hotel' ? 'primary' : 'ghost'), borderRadius: 0, border: 'none' }}>Hotel Wise</button>
                <button onClick={() => setOtaSort({ key: 'ota', dir: 'asc' })} style={{ ...btn(otaSort.key === 'ota' ? 'primary' : 'ghost'), borderRadius: 0, border: 'none' }}>OTA Wise</button>
              </div>
              <select value={otaFilterChannel} onChange={(e) => setOtaFilterChannel(e.target.value)} style={sel} title="Filter by OTA channel">
                <option value="">All OTAs</option>
                {otaChannelOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              {(otaRows.length > 0 || ownOtaRows.length > 0) && (
                <button style={btn('ghost')} onClick={() => downloadCsv('rate-shopper-ota-matrix.csv', toCsv(
                  [
                    ...ownOtaRows.flatMap((o) => otaMatrix.dates.map((dt) => ({ hotel: 'Your Hotel', channel: o.channel, date: dt, rate: o.cells[dt]?.sold_out ? 'SOLD OUT' : (o.cells[dt]?.rate ?? '') }))),
                    ...otaRows.flatMap((c) => otaMatrix.dates.map((dt) => ({ hotel: c.hotel_name, channel: c.channel, date: dt, rate: c.cells[dt]?.sold_out ? 'SOLD OUT' : (c.cells[dt]?.rate ?? '') }))),
                  ],
                  [{ label: 'Hotel', key: 'hotel' }, { label: 'OTA channel', key: 'channel' }, { label: 'Date', key: 'date' }, { label: 'Rate', key: 'rate' }]))}>⭳ CSV</button>
              )}
            </div>
          </div>
          {(otaMatrix.competitors.length === 0 && otaMatrix.own.length === 0) || otaMatrix.dates.length === 0 ? (
            <div style={emptyBox}>No per-channel competitor observations for this window.</div>
          ) : otaRows.length === 0 && ownOtaRows.length === 0 ? (
            <div style={emptyBox}>No rows match the selected OTA filter.</div>
          ) : (
            <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border)', borderRadius: 8 }}>
              <table style={{ ...table, minWidth: 640 }}>
                <thead>
                  <tr>
                    <th style={{ ...th, position: 'sticky', left: 0, zIndex: 2, background: 'var(--theme-surface-2, #f1f5f9)', minWidth: 150, cursor: 'pointer' }} {...clickable(() => toggleOtaSort('hotel'), 'Sort by hotel')}>Hotel{otaSortArrow('hotel')}</th>
                    <th style={{ ...th, minWidth: 90, cursor: 'pointer' }} {...clickable(() => toggleOtaSort('ota'), 'Sort by OTA channel')}>OTA{otaSortArrow('ota')}</th>
                    {otaMatrix.dates.map((dt) => (
                      <th key={dt} style={{ ...th, textAlign: 'center' }}>
                        {shortDate(dt)}<div style={{ fontSize: 10, fontWeight: 400, color: 'var(--theme-text-muted)' }}>{dow(dt)}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ownOtaRows.map((o) => (
                    <tr key={`own|${o.channel}`} style={{ background: '#eef2ff' }}>
                      <td style={{ ...td, position: 'sticky', left: 0, background: '#eef2ff', fontWeight: 800, color: '#3730a3' }}>★ Your Hotel</td>
                      <td style={{ ...td, fontWeight: 700, fontSize: 12, color: chanColor(o.channel) }}>{o.channel}</td>
                      {otaMatrix.dates.map((dt) => {
                        const cell = o.cells[dt];
                        if (!cell) return <td key={dt} style={{ ...td, textAlign: 'center', color: 'var(--theme-text-muted)', fontSize: 11 }}>—</td>;
                        if (cell.sold_out) return <td key={dt} style={{ ...td, textAlign: 'center' }}><span style={chip('rgba(239,68,68,0.14)', 'var(--theme-danger)')}>SOLD OUT</span></td>;
                        return <td key={dt} style={{ ...td, textAlign: 'center', fontWeight: 800, color: '#3730a3' }}>{inr(cell.rate)}</td>;
                      })}
                    </tr>
                  ))}
                  {otaRows.map((c) => (
                    <tr key={`${c.competitor_id}|${c.channel}`}>
                      <td style={{ ...td, position: 'sticky', left: 0, background: 'var(--theme-surface)', fontWeight: 600 }}>{c.hotel_name}</td>
                      <td style={{ ...td, fontWeight: 600, fontSize: 12, color: chanColor(c.channel) }}>{c.channel}</td>
                      {otaMatrix.dates.map((dt) => {
                        const cell = c.cells[dt];
                        if (!cell) return <td key={dt} style={{ ...td, textAlign: 'center', color: 'var(--theme-text-muted)', fontSize: 11 }}>—</td>;
                        if (cell.sold_out) return <td key={dt} style={{ ...td, textAlign: 'center' }}><span style={chip('rgba(239,68,68,0.14)', 'var(--theme-danger)')}>SOLD OUT</span></td>;
                        return <td key={dt} style={{ ...td, textAlign: 'center', fontWeight: 600 }}>{inr(cell.rate)}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* RS-05 Live Shop */}
      {!loading && tab === 'shop' && (
        <div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
            <button onClick={runShop} disabled={shopping || shopMeta.syncs_remaining_today <= 0} style={btn('primary')} title={shopMeta.syncs_remaining_today <= 0 ? 'Daily sync limit reached — try again tomorrow' : ''}>
              {shopping ? 'Requesting…' : '⚡ Run Rate Shop now'}
            </button>
            <button onClick={loadTab} style={btn('ghost')}>↻ Refresh status</button>
            <span style={chip(shopMeta.syncs_remaining_today > 0 ? 'rgba(37,99,235,0.12)' : 'rgba(239,68,68,0.14)', shopMeta.syncs_remaining_today > 0 ? '#2563eb' : 'var(--theme-danger)')}>
              {shopMeta.syncs_used_today}/{shopMeta.max_syncs_per_day} syncs used today
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', marginBottom: 12 }}>
            Pushes your competitor roster to the Rate Data Service; fresh rates arrive asynchronously and update the snapshots.
            {shopMeta.last_synced_at && <> · Last synced {new Date(shopMeta.last_synced_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</>}
          </div>
          <h3 style={{ fontSize: 14, margin: '6px 0' }}>Recent shop activity</h3>
          {shopLog.length === 0 ? (
            <div style={emptyBox}>No shop activity yet.</div>
          ) : (
            <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border)', borderRadius: 8 }}>
              <table style={table}>
                <thead><tr>{['When', 'Event', 'Rows', 'Outcome', 'Note'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {shopLog.map((l, i) => (
                    <tr key={i}>
                      <td style={td}>{ago(l.created_at)}</td>
                      <td style={td}>{l.event_type}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{l.row_count}</td>
                      <td style={td}><span style={chip(l.outcome === 'ACCEPTED' ? 'rgba(22,163,74,0.14)' : l.outcome === 'PARTIAL' ? 'rgba(202,138,4,0.14)' : 'rgba(239,68,68,0.14)', l.outcome === 'ACCEPTED' ? 'var(--theme-success)' : l.outcome === 'PARTIAL' ? '#ca8a04' : 'var(--theme-danger)')}>{l.outcome || '—'}</span></td>
                      <td style={{ ...td, fontSize: 11, color: 'var(--theme-text-muted)' }}>{l.error_message || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* M4 · Alerts */}
      {!loading && tab === 'alerts' && alerts && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {['HIGH', 'MEDIUM', 'LOW'].map((s) => <span key={s} style={sevChip(s)}>{s}: {alerts.counts[s] || 0}</span>)}
              <span style={chip('rgba(37,99,235,0.12)', '#2563eb')}>Price Opportunities: {alerts.price_opportunities || 0}</span>
            </div>
            {alerts.alerts.length > 0 && (
              <button style={btn('ghost')} onClick={() => downloadCsv('rate-shopper-alerts.csv', toCsv(alerts.alerts,
                [{ label: 'Severity', key: 'severity' }, { label: 'Type', key: 'type' }, { label: 'Date', key: 'stay_date' }, { label: 'Message', key: 'message' }]))}>⭳ CSV</button>
            )}
          </div>
          {alerts.alerts.length === 0 ? (
            <div style={emptyBox}>No active alerts for {alerts.meal} over the next {days} days.</div>
          ) : (
            <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border)', borderRadius: 8 }}>
              <table style={table}>
                <thead><tr>{['Severity', 'Type', 'Date', 'Message'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {alerts.alerts.map((a, i) => (
                    <tr key={i} style={a.stay_date ? { cursor: 'pointer' } : undefined} {...(a.stay_date ? clickable(() => openDate(a.stay_date), a.message) : {})}>
                      <td style={td}><span style={sevChip(a.severity)}>{a.severity}</span></td>
                      <td style={{ ...td, fontSize: 11 }}>{a.type.replace(/_/g, ' ').toLowerCase()}</td>
                      <td style={td}>{a.stay_date ? shortDate(a.stay_date) : '—'}</td>
                      <td style={td}>{a.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* RS-06 · Parity Monitor */}
      {!loading && tab === 'parity' && parity && (
        <div>
          <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', marginBottom: 8 }}>
            Own <b>direct</b> rate (PMS) vs own <b>distributed</b> rate (EASEROOM CM). A mismatch beyond tolerance is a rate-parity exception.
          </div>
          {!parity.has_cm_data ? (
            <div style={emptyBox}>{parity.note}</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={kpiCard}><div style={{ fontSize: 20, fontWeight: 700, color: parity.summary.violations ? 'var(--theme-danger)' : 'var(--theme-success)' }}>{parity.summary.violations}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Parity exceptions</div></div>
                <div style={kpiCard}><div style={{ fontSize: 20, fontWeight: 700 }}>{parity.summary.checked}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Rates checked</div></div>
                {parity.exceptions.length > 0 && (
                  <button style={btn('ghost')} onClick={() => downloadCsv('rate-shopper-parity.csv', toCsv(parity.exceptions,
                    [{ label: 'Date', key: 'stay_date' }, { label: 'Room', key: 'room' }, { label: 'Meal', key: 'meal_plan_code' }, { label: 'Direct', key: 'direct_rate' }, { label: 'Distributed', key: 'distributed_rate' }, { label: 'Difference', key: 'difference' }, { label: 'Kind', key: 'kind' }]))}>⭳ CSV</button>
                )}
              </div>
              {parity.exceptions.length === 0 ? (
                <div style={emptyBox}>✅ In parity — no exceptions beyond {parity.tolerance_pct}% tolerance.</div>
              ) : (
                <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border)', borderRadius: 8 }}>
                  <table style={table}>
                    <thead><tr>{['Date', 'Room', 'Meal', 'Direct', 'Distributed', 'Difference'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {parity.exceptions.map((e, i) => (
                        <tr key={i}>
                          <td style={td}>{shortDate(e.stay_date)}</td>
                          <td style={td}>{e.room}</td>
                          <td style={td}>{e.meal_plan_code}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{inr(e.direct_rate)}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{inr(e.distributed_rate)}</td>
                          <td style={{ ...td, textAlign: 'right', color: deltaColor(e.difference) }}>{signed(e.difference)} <span style={{ fontSize: 10, color: 'var(--theme-text-muted)' }}>{e.kind === 'DIRECT_HIGHER' ? 'direct↑' : 'direct↓'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* M4 · Sources / freshness */}
      {!loading && tab === 'sources' && sources && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={kpiCard}><div style={{ fontSize: 16, fontWeight: 700 }}>{ago(sources.feed.last_batch)}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>Competitor feed · {sources.feed.batches_7d} batches/7d</div></div>
            <div style={kpiCard}><div style={{ fontSize: 16, fontWeight: 700 }}>{ago(sources.cm_own_rate.last_sync)}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>CM own-rate sync · {sources.cm_own_rate.cached_rows} rows</div></div>
          </div>
          <h3 style={{ fontSize: 14, margin: '6px 0' }}>Competitors</h3>
          {sources.competitors.length === 0 ? (
            <div style={emptyBox}>No competitors configured.</div>
          ) : (
            <div style={{ overflowX: 'auto', border: '1px solid var(--theme-border)', borderRadius: 8 }}>
              <table style={table}>
                <thead><tr>{['Hotel', 'Status', 'Last seen', 'Days covered'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {sources.competitors.map((c) => (
                    <tr key={c.id}>
                      <td style={td}>{c.hotel_name}</td>
                      <td style={td}><span style={chip(c.resolution_status === 'ACTIVE' ? 'rgba(22,163,74,0.14)' : 'rgba(202,138,4,0.14)', c.resolution_status === 'ACTIVE' ? 'var(--theme-success)' : '#ca8a04')}>{c.resolution_status || 'PENDING'}</span></td>
                      <td style={td}>{ago(c.last_seen)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{c.days_covered || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* RS-04 Date Detail drawer */}
      {drawer && (
        <div style={overlay} onClick={() => setDrawer(null)}>
          <div style={drawerPanel} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>{shortDate(drawer.date)} <span style={{ color: 'var(--theme-text-muted)', fontWeight: 400 }}>· {roomName} · {meal}</span></h3>
              <button onClick={() => setDrawer(null)} style={btn('ghost')}>✕</button>
            </div>
            {!detail ? <div style={{ padding: 20, color: 'var(--theme-text-muted)' }}>Loading…</div> : (
              <>
                <div style={{ display: 'flex', gap: 6, margin: '8px 0', flexWrap: 'wrap' }}>
                  {[['market', 'Market'], ['rooms', 'Room comparison'], ['channels', 'Channels'], ['history', 'History']].map(([k, l]) => (
                    <button key={k} onClick={() => setDetailTab(k)} style={{ ...btn(detailTab === k ? 'primary' : 'ghost'), fontSize: 12, padding: '4px 10px' }}>{l}</button>
                  ))}
                </div>

                {detailTab === 'market' && (
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {detail.market ? [
                      ['Your rate', inr(detail.market.own_rate)],
                      ['Market median', inr(detail.market.market_median)],
                      ['Cheapest comp', inr(detail.market.cheapest_competitor)],
                      ['Your rank', detail.market.own_rank != null ? `#${detail.market.own_rank}/${detail.market.comparable_count + 1}` : 'n/a'],
                    ].map(([l, v]) => (
                      <div key={l} style={kpiCard}><div style={{ fontSize: 18, fontWeight: 700 }}>{v}</div><div style={{ fontSize: 12, color: 'var(--theme-text-muted)' }}>{l}</div></div>
                    )) : <div style={emptyBox}>No comparison for this date.</div>}
                  </div>
                )}

                {detailTab === 'rooms' && (
                  (detail.room_comparison || []).length === 0 ? <div style={emptyBox}>No mapped competitors.</div> :
                  detail.room_comparison.map((c) => (
                    <div key={c.competitor_id} style={rowLine}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{c.hotel_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--theme-text-muted)' }}>{c.competitor_room_name || '—'} · {c.channel || ''} {c.priority_used ? `· P${c.priority_used}` : ''}</div>
                      </div>
                      {c.is_comparable && c.rate != null
                        ? <span style={{ fontWeight: 700 }}>{inr(c.rate)}</span>
                        : <span style={chip('rgba(148,163,184,0.18)', 'var(--theme-text-muted)')}>{(c.nc_reason || 'N/C').replace(/_/g, ' ')}</span>}
                    </div>
                  ))
                )}

                {detailTab === 'channels' && (
                  (detail.channels || []).length === 0 ? <div style={emptyBox}>No raw channel observations.</div> :
                  <div style={{ overflowX: 'auto' }}>
                    <table style={table}>
                      <thead><tr>{['Hotel', 'Channel', 'Room', 'Rate', 'Flags'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {detail.channels.map((c, i) => (
                          <tr key={i}>
                            <td style={td}>{c.hotel_name}</td>
                            <td style={td}>{c.ota_channel}</td>
                            <td style={{ ...td, fontSize: 11 }}>{c.room_category}</td>
                            <td style={{ ...td, textAlign: 'right' }}>{c.is_sold_out ? <span style={chip('rgba(239,68,68,0.14)', 'var(--theme-danger)')}>SOLD OUT</span> : inr(c.rate_amount)}</td>
                            <td style={{ ...td, fontSize: 11, color: 'var(--theme-text-muted)' }}>{[c.refundable ? 'ref' : 'non-ref', c.tax_inclusive ? 'tax-inc' : 'tax-exc'].join(' · ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {detailTab === 'history' && (
                  (detail.history || []).length === 0 ? <div style={emptyBox}>No snapshot history for this date.</div> :
                  <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                    {detail.history.map((h, i) => (
                      <div key={i} style={{ ...rowLine, fontSize: 12 }}>
                        <span style={{ color: 'var(--theme-text-muted)' }}>{new Date(h.captured_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                        <span>{h.channel || ''} {h.priority_used ? `P${h.priority_used}` : ''}</span>
                        <span style={{ fontWeight: 600 }}>{h.is_comparable ? inr(h.competitor_rate) : 'N/C'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* styles */
const sel = { padding: '6px 10px', borderRadius: 6, border: '1px solid var(--theme-border)', background: 'var(--theme-surface)', color: 'var(--theme-text)', fontSize: 13 };
const kpiCard = { flex: '1 1 130px', minWidth: 130, padding: '10px 14px', borderRadius: 8, border: '1px solid var(--theme-border)', background: 'var(--theme-surface)' };
const table = { borderCollapse: 'separate', borderSpacing: 0, width: '100%', fontSize: 13 };
const th = { textAlign: 'left', padding: '8px 12px', fontSize: 12, borderBottom: '1px solid var(--theme-border)', background: 'var(--theme-surface-2, #f1f5f9)', color: 'var(--theme-text)' };
const td = { padding: '8px 12px', borderBottom: '1px solid var(--theme-border)', verticalAlign: 'top' };
const rowLine = { display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between', padding: '8px 4px', borderBottom: '1px solid var(--theme-border)' };
const emptyBox = { padding: 24, textAlign: 'center', color: 'var(--theme-text-muted)', border: '1px dashed var(--theme-border)', borderRadius: 8 };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' };
const drawerPanel = { width: 'min(560px, 100%)', maxWidth: 560, height: '100%', overflowY: 'auto', background: 'var(--theme-surface)', color: 'var(--theme-text)', padding: 18, boxShadow: '-4px 0 24px rgba(0,0,0,0.2)' };
function banner(kind) {
  const m = { danger: { bg: 'rgba(239,68,68,0.1)', bd: 'var(--theme-danger)', fg: 'var(--theme-danger)' } }[kind];
  return { margin: '8px 0', padding: '8px 12px', borderRadius: 6, fontSize: 13, background: m.bg, border: `1px solid ${m.bd}`, color: m.fg };
}
function btn(kind) {
  const base = { padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid transparent' };
  const m = {
    primary: { background: 'var(--theme-primary)', color: '#fff', borderColor: 'var(--theme-primary)' },
    ghost: { background: 'transparent', color: 'var(--theme-text)', borderColor: 'var(--theme-border)' },
  };
  return { ...base, ...m[kind] };
}

export default RateShopper;
